import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ValidationErrorCode } from '../../../common/enums/validationErrorCode';
import { fetchJson } from '../../store/fetch';
import { useStateWithPartialSet } from './useStateWithPartialSet';

interface IFetchOptions {
    allowedHTTPErrorCodes?: number[];
    allowErrorCodes?: ValidationErrorCode[];
    /* If the abortController should be disabled, defaults to false */
    disableAbort?: boolean;
    /* If the route should be called on mount, defaults to true */
    fetchOnMount?: boolean;
    /* If the route should be called on url change, defaults to true */
    fetchOnUrlChange?: boolean;
    /* The http method, defaults to GET */
    httpMethod?: 'GET' | 'POST' | 'DELETE';
    /* If the error should be thrown again (this is then caught by the default error page), defaults to true */
    rethrowError?: boolean;
}

interface IFetchOptionsWithBody extends IFetchOptions {
    body: any;
    httpMethod: 'POST';
}

export function useFetch<T = unknown>(
    url: string,
    fetchOptions: IFetchOptionsWithBody
): [IFetchState<T>, () => Promise<void>];
export function useFetch<T = unknown>(url: string, fetchOptions?: IFetchOptions): [IFetchState<T>, () => Promise<void>];
export function useFetch<T = unknown>(
    url: string,
    fetchOptions?: IFetchOptions | IFetchOptionsWithBody
): [IFetchState<T>, () => Promise<void>] {
    return useFetchInternal<T>(url, defaultFetchOptions(fetchOptions));
}

function defaultFetchOptions(
    fetchOptions?: IFetchOptions | IFetchOptionsWithBody
): Required<IFetchOptions> | Required<IFetchOptionsWithBody> {
    return {
        httpMethod: 'GET',
        disableAbort: false,
        rethrowError: true,
        fetchOnMount: true,
        allowErrorCodes: [],
        allowedHTTPErrorCodes: [],
        fetchOnUrlChange: true,
        ...fetchOptions,
    };
}

interface IFetchState<T> {
    error: Error | null;
    errorCode?: string;
    isFetching: boolean;
    message?: string;
    responseData: T | null;
    status?: number;
}

function useFetchInternal<T>(
    url: string,
    fetchOptions: Required<IFetchOptions> | Required<IFetchOptionsWithBody>
): [IFetchState<T>, () => Promise<void>] {
    const [fetchState, setFetchState] = useStateWithPartialSet<IFetchState<T>>({
        responseData: null,
        error: null,
        isFetching: fetchOptions.fetchOnMount,
    });
    const [initialFetch, setInitialFetch] = useState(true);
    const abortController = useMemo(() => new AbortController(), []);

    const changeArray = [abortController, (fetchOptions as IFetchOptionsWithBody).body];

    if (fetchOptions.fetchOnUrlChange) {
        changeArray.push(url);
    }

    const doFetch = useCallback(async () => {
        setFetchState({ error: null, isFetching: true, status: undefined, errorCode: undefined, message: undefined });
        try {
            const response = await fetchJson(
                url,
                fetchOptions.httpMethod,
                (fetchOptions as IFetchOptionsWithBody).body || undefined,
                fetchOptions.allowErrorCodes,
                fetchOptions.allowedHTTPErrorCodes,
                abortController.signal
            );
            setFetchState({
                responseData: response.json,
                error: null,
                isFetching: false,
                errorCode: response.errorCode,
                message: response.json?.message,
                status: response.status,
            });
        } catch (e: any) {
            setFetchState({ responseData: null, error: e, isFetching: false });
            if (fetchOptions.rethrowError) {
                throw e;
            }
        }
    }, [...changeArray, fetchOptions.httpMethod, fetchOptions.rethrowError]);

    useEffect(() => {
        if (fetchOptions.fetchOnMount || (!fetchOptions.fetchOnMount && !initialFetch)) {
            doFetch();
        }
        if (initialFetch) {
            setInitialFetch(false);
        }

        return () => {
            if (!fetchOptions.disableAbort && fetchState.isFetching) {
                return abortController.abort();
            }
        };
    }, changeArray);

    return [fetchState, doFetch];
}
